import { pageSize } from "./../../Components/Common/styles/table";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

type PaginationProps = {
  page: number;
  pageSize: number;
};

export interface ParticipantsState {
  loading: boolean;
  participantsData: any[];
  totalParticipants: number;
  paginationModel: PaginationProps;
  toggleLoader: boolean;
  sortOrder: string;
  sortColumn: string;
  siteIds: string[];
  status: string;
  isLocked: string;
  cohortId: string;
  columns: {
    label: string;
    varname: string;
    questionLabel: string;
    questionType: string;
    questionChoices: { label: string; id: string }[];
  }[];
  selectedParticipantIds: string[];
  customHeaderValues: { key: string; value: string }[];
}

const initialState: ParticipantsState = {
  loading: true,
  toggleLoader: false,
  paginationModel: {
    page: 0,
    pageSize: pageSize,
  },
  participantsData: [],
  totalParticipants: 0,
  sortOrder: "",
  sortColumn: "",
  siteIds: [],
  status: "",
  isLocked: "",
  cohortId: "",
  columns: [],
  selectedParticipantIds: [],
  customHeaderValues: [],
};

export const participantsSlice = createSlice({
  name: "participants",
  initialState: initialState,
  reducers: {
    setParticipantsLoader: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setParticipantsDetails: (state, action: PayloadAction<any>) => {
      state.participantsData = action.payload;
    },
    setColumnData: (state, action: PayloadAction<any>) => {
      state.columns = action.payload;
    },
    setParticipantsCount: (state, action: PayloadAction<number>) => {
      state.totalParticipants = action.payload;
    },
    setParticipantsPage: (state, action: PayloadAction<PaginationProps>) => {
      state.paginationModel = action.payload;
    },

    setParticipantToggle: (state) => {
      state.toggleLoader = !state.toggleLoader;
    },
    setParticipantSort: (
      state,
      action: PayloadAction<{ column: string; order: string }>
    ) => {
      state.sortColumn = action.payload.column;
      state.sortOrder = action.payload.order;
    },
    setParticipantFilter: (
      state,
      action: PayloadAction<{
        siteIds: string[];
        status: string;
        isLocked: string;
        cohortId: string;
        customHeaderValues: { key: string; value: string }[];
      }>
    ) => {
      state.paginationModel.page = 0;
      state.siteIds = action.payload.siteIds;
      state.status = action.payload.status;
      state.isLocked = action.payload.isLocked;
      state.cohortId = action.payload.cohortId;
      state.customHeaderValues = action.payload.customHeaderValues;
    },
    setCohortId: (state, action: PayloadAction<string>) => {
      state.cohortId = action.payload;
      state.paginationModel.page = 0;
    },
    clearParticipantIds: (state) => {
      state.selectedParticipantIds = [];
    },
    appendParticipantIds: (
      state,
      action: PayloadAction<{
        actionType: "single" | "multiple";
        rowId?: string;
        checked: boolean;
      }>
    ) => {
      const { actionType, rowId, checked } = action.payload;
      let newSelected = state.selectedParticipantIds;
      let participantsData = state.participantsData;

      if (actionType === "single") {
        if (checked && rowId) {
          newSelected.push(rowId);
        } else {
          newSelected = newSelected.filter((c) => c !== rowId);
        }
      } else if (actionType === "multiple") {
        participantsData.forEach((p) => {
          const found = newSelected.find((s: any) => s === p.id);
          if (!found) {
            newSelected.push(p.id);
          }
        });
      }

      state.selectedParticipantIds = newSelected;
    },
    reset: () => initialState,
  },
});

export const {
  setParticipantsLoader,
  setParticipantsDetails,
  setParticipantsCount,
  setParticipantsPage,
  setParticipantToggle,
  setParticipantSort,
  setParticipantFilter,
  setColumnData,
  setCohortId,
  clearParticipantIds,
  appendParticipantIds,
  reset,
} = participantsSlice.actions;

export default participantsSlice.reducer;
